import { Resources, type TFunction } from "Translations/Resources";
import type { TimeRange } from "Types/OpeningHours";

/**
 * Formats a date to relative time using translations
 * @param date - The date to format (can be Date object or UTC string)
 * @param t - Translation function
 * @returns Formatted relative time string
 */
export const formatRelativeTime = (
  date: Date | string,
  t: TFunction,
): string => {
  const resources = Resources.Common.Time.TimePassed;

  const now = new Date();
  // If it's a string, create a Date object (JavaScript automatically handles UTC strings)
  const localDateTime = typeof date === "string" ? new Date(date) : date;
  const diffInMs = now.getTime() - localDateTime.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonthsRaw = Math.floor(diffInDays / 30);
  const diffInMonths = Math.max(diffInMonthsRaw, 1);
  const diffInYearsRaw = Math.floor(diffInDays / 365);
  const diffInYears = Math.max(diffInYearsRaw, 1);

  if (diffInMinutes < 1) {
    return t(resources.Now);
  }
  if (diffInMinutes < 60) {
    return t(resources.Minute, { count: diffInMinutes });
  }
  if (diffInHours < 24) {
    return t(resources.Hour, { count: diffInHours });
  }
  if (diffInDays < 7) {
    return t(resources.Day, { count: diffInDays });
  }
  if (diffInWeeks < 4) {
    return t(resources.Week, { count: diffInWeeks });
  }
  if (diffInMonths <= 12) {
    return t(resources.Month, { count: diffInMonths });
  }
  return t(resources.Year, { count: diffInYears });
};

/**
 * Formats remaining time using translations, combining all provided time units
 * @param params - Object containing time units (years, months, weeks, days, hours, minutes)
 * @param t - Translation function
 * @returns Formatted time string with all provided units (e.g., "1 year 3 hours 4 minutes")
 */
export const formatRemainingRelativeTime = (
  {
    minutes,
    hours,
    days,
    weeks,
    months,
    years,
  }: {
    minutes?: number;
    hours?: number;
    days?: number;
    weeks?: number;
    months?: number;
    years?: number;
  },
  t: TFunction,
): string => {
  const { Year, Month, Week, Day, Hour, Minute } =
    Resources.Common.Time.TimeRemaining;

  const timeUnits = [
    { value: years, translationKey: Year },
    { value: months, translationKey: Month },
    { value: weeks, translationKey: Week },
    { value: days, translationKey: Day },
    { value: hours, translationKey: Hour },
    { value: minutes, translationKey: Minute },
  ] as const;

  return timeUnits
    .filter(({ value }) => !!value)
    .map(({ translationKey, value }) => t(translationKey, { count: value }))
    .join(" ");
};

export const MS_TIME = {
  second: 1000,
  halfMinute: 1000 * 30,
  minute: 1000 * 60,
  halfHour: 1000 * 60 * 30,
  hour: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
};

/**
 * Formats a time string from "HH:mm:ss" to "HH:mm" format
 * @param timeString - Time string in format "HH:mm:ss" or "HH:mm"
 * @returns Formatted time string in "HH:mm" format
 */
export const formatTime = (timeString: string): string => {
  const parts = timeString.split(":");
  return `${parts[0]}:${parts[1]}`;
};

/**
 * Checks if the time range is valid (opening time is before closing time)
 * @param range - The time range to check
 * @returns True if the time range is valid or empty, false otherwise
 */
export const checkTimeRangeValidity = ({ openTime, closeTime }: TimeRange) =>
  openTime && closeTime ? openTime < closeTime : true;

export const checkTimeRangesValidity = (ranges: Array<TimeRange>) => {
  if (ranges.length === 0) {
    return true;
  }

  return ranges.every(checkTimeRangeValidity);
};
